const getDefaultWindow = () => (typeof window !== 'undefined' ? window : null);

const getLocationObject = (windowObject = getDefaultWindow()) => {
  try {
    return windowObject?.location || null;
  } catch (_) {
    return null;
  }
};

const getLocationPathname = (windowObject = getDefaultWindow()) =>
  getLocationObject(windowObject)?.pathname || '';

const getAccountIdFromPath = (pathname = '') => {
  const match = String(pathname).match(/\/app\/accounts\/([^/?#]+)/);

  return match?.[1] || '';
};

const getAccountIdFromLocation = (windowObject = getDefaultWindow()) =>
  getAccountIdFromPath(getLocationPathname(windowObject));

const isAccountScopedPath = (pathname = '') =>
  String(pathname).includes('/app/accounts/');

const isAccountScopedLocation = (windowObject = getDefaultWindow()) =>
  isAccountScopedPath(getLocationPathname(windowObject));

const getHistoryLength = (windowObject = getDefaultWindow()) => {
  try {
    return Number(windowObject?.history?.length) || 0;
  } catch (_) {
    return 0;
  }
};

const safeOpenWindow = (
  url,
  target = '_blank',
  features = undefined,
  windowObject = getDefaultWindow()
) => {
  try {
    if (url == null || typeof windowObject?.open !== 'function') {
      return null;
    }

    return windowObject.open(url, target, features);
  } catch (_) {
    return null;
  }
};

const safeWriteWindowDocument = (targetWindow, html = '') => {
  try {
    const targetDocument = targetWindow?.document;
    if (
      typeof targetDocument?.write !== 'function' ||
      typeof targetDocument?.close !== 'function'
    ) {
      return false;
    }

    targetDocument.write(html);
    targetDocument.close();
    return true;
  } catch (_) {
    return false;
  }
};

const safePrintWindow = (targetWindow) => {
  try {
    if (typeof targetWindow?.print !== 'function') {
      return false;
    }

    targetWindow.print();
    return true;
  } catch (_) {
    return false;
  }
};

const safeCloseWindow = (targetWindow) => {
  try {
    if (typeof targetWindow?.close !== 'function') {
      return false;
    }

    targetWindow.close();
    return true;
  } catch (_) {
    return false;
  }
};

const safeRedirectTo = (url, windowObject = getDefaultWindow()) => {
  if (!url) return false;

  try {
    const location = getLocationObject(windowObject);
    if (!location) return false;

    location.href = url;
    return true;
  } catch (_) {
    return false;
  }
};

const safeReloadWindow = (windowObject = getDefaultWindow()) => {
  try {
    const location = getLocationObject(windowObject);
    if (typeof location?.reload !== 'function') {
      return false;
    }

    location.reload();
    return true;
  } catch (_) {
    return false;
  }
};

const safePushHistoryState = (
  state,
  title,
  url,
  windowObject = getDefaultWindow()
) => {
  try {
    if (typeof windowObject?.history?.pushState !== 'function') {
      return false;
    }

    windowObject.history.pushState(state, title, url);
    return true;
  } catch (_) {
    return false;
  }
};

const getGlobalConfig = (windowObject = getDefaultWindow()) => {
  try {
    return windowObject?.globalConfig || {};
  } catch (_) {
    return {};
  }
};

const getOtelloConfig = (windowObject = getDefaultWindow()) => {
  try {
    return windowObject?.otelloConfig || {};
  } catch (_) {
    return {};
  }
};

export {
  getAccountIdFromLocation,
  getAccountIdFromPath,
  getGlobalConfig,
  getHistoryLength,
  getLocationObject,
  getLocationPathname,
  getOtelloConfig,
  isAccountScopedLocation,
  isAccountScopedPath,
  safeOpenWindow,
  safeCloseWindow,
  safePrintWindow,
  safePushHistoryState,
  safeRedirectTo,
  safeReloadWindow,
  safeWriteWindowDocument,
};
